import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { configurationSelectors, State } from '@wam/shared';
import { take, tap } from 'rxjs/operators';
import { newTracker, trackPageView, trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import { Toggles } from '@app/shared/models/toggles.enum';
import { combineLatest, filter } from 'rxjs';
import { authenticationSelectors } from '@wam/authentication';
import {
  ButtonClickTrackingPlugin,
  disableButtonClickTracking,
  enableButtonClickTracking,
} from '@snowplow/browser-plugin-button-click-tracking';
import { FormTrackingPlugin } from '@snowplow/browser-plugin-form-tracking';
import { SettingsService } from '@app/settings/settings.service';
import { ResourceAndActivitiesTrackingEvent } from '@app/shared/snowplow/resource-and-activities-tracking-event';
import { ResourceAndActivitiesSearchTrackingEvent } from '@app/shared/snowplow/resource-and-activities--search-tracking-event';
import {
  disableLinkClickTracking,
  enableLinkClickTracking,
  LinkClickTrackingPlugin,
} from '@snowplow/browser-plugin-link-click-tracking';

@Injectable({
  providedIn: 'root',
})
export class SnowplowService {
  constructor(
    protected store: Store<State>,
    private settingsService: SettingsService,
  ) {}

  initialize() {
    combineLatest([
      this.store.select(configurationSelectors.isEnabled(Toggles.SEND_SNOWPLOW_EVENTS)),
      this.store.select(authenticationSelectors.getCurrentUser),
    ])
      .pipe(filter(([snowPlowEnabled, user]) => !!user))
      .subscribe(([snowPlowEnabled, user]) => {
        if (snowPlowEnabled) {
          const tracker = newTracker(
            'main-tracker',
            this.settingsService.settings.snowplow.collectorUrl,
            {
              appId: user.application,
              respectDoNotTrack: true,
              anonymousTracking: false,
              contexts: {
                session: true,
              },
              plugins: [
                ButtonClickTrackingPlugin(),
                FormTrackingPlugin(),
                LinkClickTrackingPlugin(),
              ],
              cookieSameSite: 'Lax',
            },
          );

          tracker?.setUserId(user.sub);
        }
      });
  }

  trackPageView() {
    this.store
      .select(configurationSelectors.isEnabled(Toggles.SEND_SNOWPLOW_EVENTS))
      .pipe(
        take(1),
        tap((snowplowEnabled) => {
          if (snowplowEnabled) {
            trackPageView();
          }
        }),
      )
      .subscribe();
  }

  enableButtonClickTrackingForEnrollmentFunnel(): void {
    const enrollmentButtonIdFilter = {
      filter: (element: HTMLElement): boolean => {
        return (
          element.id === 'eula-accept' ||
          element.id === 'profile_setup_submit' ||
          element.id === 'child_mini_form_submit_btn' ||
          element.id.match(/enrollment_.*_next_btn/) != null ||
          element.id === 'enrollment_submit_btn' ||
          element.id.startsWith('home-page-checklist')
        );
      },
    };

    this.store
      .select(configurationSelectors.isEnabled(Toggles.SEND_SNOWPLOW_EVENTS))
      .pipe(
        take(1),
        tap((snowplowEnabled) => {
          if (snowplowEnabled) {
            enableButtonClickTracking(enrollmentButtonIdFilter);
            enableLinkClickTracking({ options: enrollmentButtonIdFilter });
          }
        }),
      )
      .subscribe();
  }

  disableButtonClickTracking(): void {
    this.store
      .select(configurationSelectors.isEnabled(Toggles.SEND_SNOWPLOW_EVENTS))
      .pipe(
        take(1),
        tap((snowplowEnabled) => {
          if (snowplowEnabled) {
            disableButtonClickTracking();
            disableLinkClickTracking();
          }
        }),
      )
      .subscribe();
  }

  trackResourceAndActivitiesEvent(event: ResourceAndActivitiesTrackingEvent) {
    combineLatest([
      this.store.select(configurationSelectors.isEnabled(Toggles.SEND_SNOWPLOW_EVENTS)),
      this.store.select(authenticationSelectors.getCurrentUser),
    ])
      .pipe(take(1))
      .subscribe(([snowPlowEnabled, user]) => {
        if (snowPlowEnabled) {
          trackSelfDescribingEvent({
            event: {
              schema: 'iglu:com.waterford/custom_click_events/jsonschema/1-0-0',
              data: {
                button_id: event.buttonId,
                organization_id: user.organization,
                user_id: user.sub,
                user_roles: user.roles.join(','),
                document_title: event.documentTitle,
                document_id: event.documentId,
                resource_type: event.resourceType,
              },
            },
          });
        }
      });
  }

  trackResourceAndActivitiesSearchEvent(event: ResourceAndActivitiesSearchTrackingEvent) {
    combineLatest([
      this.store.select(configurationSelectors.isEnabled(Toggles.SEND_SNOWPLOW_EVENTS)),
      this.store.select(authenticationSelectors.getCurrentUser),
    ])
      .pipe(take(1))
      .subscribe(([snowPlowEnabled, user]) => {
        if (snowPlowEnabled) {
          trackSelfDescribingEvent({
            event: {
              schema: 'iglu:com.waterford/custom_search_events/jsonschema/1-0-0',
              data: {
                organization_id: user.organization,
                user_id: user.sub,
                user_roles: user.roles.join(','),
                search_string: event.searchString,
                total_count: event.totalCount,
              },
            },
          });
        }
      });
  }
}
